import * as React from 'react';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from '@material-ui/core/styles';
import { AuthContext } from './AuthContext';
import VideoDialog from './VideoDialog';
import { Card, CardContent, Button, SnackbarContent, Divider, CircularProgress } from '@material-ui/core';
import { Redirect, Link as RouterLink, useRouteMatch } from 'react-router-dom';
import AnnouncementSlider from './AnnouncementSlider';
import { AnnouncementSliderData } from './AnnouncementSliderData';
import Delta_format from './../images/Delta_format.png';
import Intro from './../images/DeltaJSON_Intro.png';
import JSONArray from './../images/JSONArray.png';
import WarningIcon from '@material-ui/icons/Warning';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import { DeltaXMLColors } from '../styling/DeltaColors';
import { CustomerSubscription, SubsData, CustomerSubscriptionStatus } from './SubscriptionData';
import { SubscriptionRequestData, SubscriptionRequestStatus } from './Paperbase';
import TokenDialog from './TokenDialog';
import { NavigatorData, Operations } from './NavigatorData';
import { useHistory } from "react-router-dom";
import { Themes } from '../styling/Themes';

const styles = (theme: Theme) =>
  createStyles({
    welcomePaper: {
      padding: '20px;',
      overflow: 'hidden',
      margin: 'auto',
      maxWidth: 1280,
      maxHeight: 700,
    },
    cardRoot: {
      display: 'flex',
      margin: '5px',
      flexDirection: 'column',
      alignItems: 'center',
      height: '360px',
      backgroundColor: DeltaXMLColors.CONCRETE
    },
    cardRoot2: {
      display: 'flex',
      margin: '5px',
      flexDirection: 'column',
      alignItems: 'center',
    },
    cardContent: {
      textAlign: 'center',
      padding: '10px'
    },
    title: {
      textAlign: 'center',
      padding: '5px'
    },
    para: {
      textAlign: 'left',
    },
    warningMessage: {
      backgroundColor: DeltaXMLColors.ORANGE_BACKGROUND,
      color: DeltaXMLColors.OXFORD_BLUE,
      height: '110px'
    },
    infoMessage: {
      backgroundColor: DeltaXMLColors.BLUE_BACKGROUND,
      color: DeltaXMLColors.OXFORD_BLUE,
      height: '110px'
    },
    errorMessage: {
      backgroundColor: DeltaXMLColors.ORANGE_BACKGROUND,
      color: DeltaXMLColors.RED,
      height: '110px'
    },
    innerMessage: {
      width: '100%',
      display: 'flex'
    },
    warningIcon: {
      color: DeltaXMLColors.ORANGE,
      marginRight: '10px'
    },
    infoIcon: {
      color: DeltaXMLColors.FOUNTAIN_BLUE,
      marginRight: '10px'
    },
    errorIcon: {
      color: DeltaXMLColors.RED,
      marginRight: '10px'
    },
    actionButton: {
      width: '150px',
      marginBottom: '10px'
    },
    welcomeActionBtns: {
      width: '175px'
    },
    divider1: {
      backgroundColor: DeltaXMLColors.GREY,
      margin: '5px 0px 5px 0px'
    },
    getTokenIcon: {
      color: DeltaXMLColors.FOUNTAIN_BLUE
    },
    progress: {
      float: 'right',
      marginLeft: '30px'
    }
  });

export interface WelcomeContentProps extends WithStyles<typeof styles> {
  customerSubscriptionData: CustomerSubscription,
  requestStatus: SubscriptionRequestData
}

const goToMyDeltaAction = (
  <Button color="primary" variant="contained" onClick={() => window.open(process.env.REACT_APP_DELTAJSON_REGISTER_URL, '_blank')}>
    Go to MyDelta
  </Button>
)

function WelcomeContent(props: WelcomeContentProps) {
  const { classes, customerSubscriptionData, requestStatus } = props;
  const auth = React.useContext(AuthContext);
  const [isTokenDialogOpened, setTokenDialogOpened] = React.useState(false);

  const handleTokenDialogOpen = () => {
    setTokenDialogOpened(true);
  }

  const handleTokenDialogClose = () => {
    setTokenDialogOpened(false);
  }; 

  const showProgressMessage = requestStatus.subscriptions.status === SubscriptionRequestStatus.notYetRequested || requestStatus.subscriptions.status === SubscriptionRequestStatus.inProgress;
  const showErrorMessage = requestStatus.subscriptions.status === SubscriptionRequestStatus.errorResponse;
  const starterSubscription: boolean = customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.FREE;
  const professionalSubscription: boolean = customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.PAID;

  const video1Heading = "Delta JSON";
  const video1URL = "https://www.youtube.com/embed/PSvTMiJF1SQ";
  const video1Thumbnail = Intro;
  const video2Heading = "Delta File";
  const video2URL = "https://www.youtube.com/embed/bmwQMbEKnm0";
  const video2Thumbnail = Delta_format;
  const video3Heading = "JSON Array";
  const video3URL = "https://www.youtube.com/embed/msQv6dp2n5s";
  const video3Thumbnail = JSONArray;

  const history = useHistory();

  let operationBtnToolTipMsg = <Typography color="inherit" variant="body2">Your current plan does not permit you to perform this operation.</Typography>;

  function downloadToken() { 
    auth.download(); 
    setTokenDialogOpened(false);
  }

  return (
    auth.isAuthenticated() ?
    <Paper className={classes.welcomePaper} style={{ overflow: 'auto' }}>
      {(showProgressMessage && auth.isAuthenticated()) && (
        <SnackbarContent className={classes.warningMessage} message={
          <div className={classes.innerMessage}>
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="body1" color="primary">
              Please wait while we fetch your subscription information...
            </Typography>
            <CircularProgress className={classes.progress} variant="indeterminate" size="30px" />
          </div>}/>
      )}
      {(!showProgressMessage && !showErrorMessage  && (customerSubscriptionData === undefined || customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.NULL) && auth.isAuthenticated()) && (
        <SnackbarContent className={classes.warningMessage} message={
          <div className={classes.innerMessage}>
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="body1" color="primary">
              You need an active subscription to perform any operation in the DeltaJSON Application.
              Please contact support@deltaxml.com.
                </Typography>
          </div>}
          action={goToMyDeltaAction} />
      )}
      {(showErrorMessage && auth.isAuthenticated()) && (
        <SnackbarContent className={classes.errorMessage} message={
          <div className={classes.innerMessage}>
            <ErrorIcon className={classes.errorIcon} />
            <Typography variant="body1" color="primary">
              Error occurred while fetching your subscription information. Please contact deltaxml support if this problem persists.
              <br/>
              {requestStatus.subscriptions.statusNumber}: {requestStatus.subscriptions.text} 
            </Typography>
          </div>}/>
      )}
      {(!showProgressMessage && !showErrorMessage && customerSubscriptionData !== undefined && customerSubscriptionData.subscriptionStatus !== CustomerSubscriptionStatus.NULL && auth.isAuthenticated()) && (
        <Card className={classes.infoMessage}>
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={1}>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Grid>
              <Grid item xs={9}>
                <Typography variant='h6' color='primary' style={{fontWeight: 'bolder'}}>
                  {SubsData.statusTextMap.get(customerSubscriptionData.subscriptionStatus)}
                </Typography>
                <Typography variant='body1' color='primary'>
                  Renewal Date: {customerSubscriptionData.expiryDate}
                  <br />
                  Usage/Total Permitted Usage: {customerSubscriptionData.usage.userApiUsageCounter}/{customerSubscriptionData.usage.userTotalUsage}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Button className={classes.actionButton} color="primary" variant="contained" onClick={() => window.open(process.env.REACT_APP_DELTAJSON_REGISTER_URL, '_blank')}>
                  Go To MyDelta
                </Button>
                {(auth.isAuthenticated() && customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.PAID) && (
                  <>
                    <TokenDialog isDialogOpened={isTokenDialogOpened} handleDialogClose={handleTokenDialogClose} download={downloadToken}/>
                    <Button color="primary" variant="contained" className={classes.actionButton} onClick={handleTokenDialogOpen}>
                      Get Token
                    </Button>
                  </>
                )}
                {(auth.isAuthenticated() && customerSubscriptionData.isNpmPackageOnly && customerSubscriptionData.subscriptionStatus !== CustomerSubscriptionStatus.PAID) && (
                  <Button color="primary" variant="contained" className={classes.actionButton} onClick={downloadToken}>
                    Download Token
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      <br/>
      <Card className={classes.cardRoot2} elevation={0}>
        <CardContent className={classes.cardContent}>
          <Typography variant='h5' color='primary' style={{ fontWeight: 'bold', fontStyle: 'italic' }}>Quick Start</Typography>
          <Divider className={classes.divider1} />
          <br/>
          <Grid container direction='row' spacing={2}>
            <Grid item>
              {!showErrorMessage && !showProgressMessage && (starterSubscription || professionalSubscription) ? 
                <Button variant='contained' color='primary' className={classes.welcomeActionBtns}
                  onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.compare)}`)}>
                  <Typography>Compare</Typography>
                </Button>
              :
                <Themes.HtmlTooltip title={operationBtnToolTipMsg} PopperProps={{ style: { marginTop: 10 } }} arrow>
                  <span>
                    <Button variant='contained' color='primary' className={classes.welcomeActionBtns} disabled
                      onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.compare)}`)}>
                      <Typography>Compare</Typography>
                    </Button>
                  </span>
                </Themes.HtmlTooltip>
              }  
            </Grid>
            <Grid item>
              {!showErrorMessage && !showProgressMessage && professionalSubscription ?
                <Button variant='contained' color='primary' className={classes.welcomeActionBtns}
                    onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.twoWayMerge)}`)}>
                  <Typography>Two Way Merge</Typography>
                </Button>
                :
                <Themes.HtmlTooltip title={operationBtnToolTipMsg} PopperProps={{ style: { marginTop: 10 } }} arrow>
                  <span>
                    <Button variant='contained' color='primary' className={classes.welcomeActionBtns} disabled
                      onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.twoWayMerge)}`)}>
                      <Typography>Two Way Merge</Typography>
                    </Button>
                  </span>
                </Themes.HtmlTooltip>
              }
            </Grid>
            <Grid item>
              {!showErrorMessage && !showProgressMessage && professionalSubscription ?
                <Button variant='contained' color='primary' className={classes.welcomeActionBtns}
                  onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.threeWayMerge)}`)}>
                  <Typography>Three Way Merge</Typography>
                </Button>
                :
                <Themes.HtmlTooltip title={operationBtnToolTipMsg} PopperProps={{ style: { marginTop: 10 } }} arrow>
                  <span>
                    <Button variant='contained' color='primary' className={classes.welcomeActionBtns} disabled
                      onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.threeWayMerge)}`)}>
                      <Typography>Three Way Merge</Typography>
                    </Button>
                  </span>
                </Themes.HtmlTooltip>      
              }
            </Grid>
            <Grid item>
              {!showErrorMessage && !showProgressMessage && professionalSubscription ?
                <Button variant='contained' color='primary' className={classes.welcomeActionBtns}
                  onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.graft)}`)}>
                  <Typography>Graft</Typography>
                </Button>
                :
                <Themes.HtmlTooltip title={operationBtnToolTipMsg} PopperProps={{ style: { marginTop: 10 } }} arrow>
                  <span>
                    <Button variant='contained' color='primary' className={classes.welcomeActionBtns} disabled
                      onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.graft)}`)}>
                      <Typography>Graft</Typography>
                    </Button>
                  </span>
                </Themes.HtmlTooltip>
              }
            </Grid>
            <Grid item>
              {!showErrorMessage && !showProgressMessage && professionalSubscription ?
                <Button variant='contained' color='primary' className={classes.welcomeActionBtns}
                  onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.patch)}`)}>
                  <Typography>Create Patch</Typography>
                </Button>
                :
                <Themes.HtmlTooltip title={operationBtnToolTipMsg} PopperProps={{ style: { marginTop: 10 } }} arrow>
                  <span>
                    <Button variant='contained' color='primary' className={classes.welcomeActionBtns} disabled
                      onClick={() => history.push(`${NavigatorData.configBaseUrl}${NavigatorData.getPathForOperation(Operations.patch)}`)}>
                      <Typography>Create Patch</Typography>
                    </Button>
                  </span>
                </Themes.HtmlTooltip>
              }
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <br/>
      <Grid container spacing={1}>
        {/* <Grid item xs={12} sm={3}>
          <Card className={classes.cardRoot} variant="elevation">
            <CardContent className={classes.cardContent}>
              <Typography className={classes.title} color="primary" variant="h5">
                Announcements
                </Typography>
              <Divider className={classes.divider1} />
              <AnnouncementSlider slides={AnnouncementSliderData.SliderData} />
            </CardContent>
          </Card>
        </Grid> */}
        <Grid item xs={12}>
          <Card className={classes.cardRoot} variant="elevation">
            <CardContent className={classes.cardContent}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={8}>
                  <Typography className={classes.title} color="primary" variant="h5">
                    Introduction
              </Typography>
                  <Divider className={classes.divider1} />
                  <Typography variant="body1" color="secondary" gutterBottom className={classes.para}>
                    DeltaJSON allows you to analyse the differences between JSON files through various operations
                such as <i>Compare</i> (two inputs) or <i>Three Way Merge</i>.
              </Typography>
                  <Typography variant="body1" color="secondary" gutterBottom className={classes.para}>
                    In the Configuration section you can upload the inputs,
                    configure the operation, and download the result. The Files section allows
                    you to view the input and result files.
              </Typography>
                  {(auth.isAuthenticated() && customerSubscriptionData !== undefined && customerSubscriptionData.subscriptionStatus !== CustomerSubscriptionStatus.NULL) ?
                    <Typography variant="body1" color="secondary" className={classes.para} gutterBottom>
                      To start a DeltaJSON operation, such as <i>Compare</i>, you first need to visit the&nbsp;
                  <RouterLink to="/configuration" style={{color: DeltaXMLColors.FOUNTAIN_BLUE}}>Configuration Page</RouterLink>.
                </Typography>
                    :
                    (!auth.isAuthenticated()) ?
                      <Typography variant="body1" color="secondary" className={classes.para} gutterBottom>
                        To use the application, please sign in.
                  </Typography>
                      :
                      <></>
                  }
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography className={classes.title} color="primary" variant="h5">
                    Videos
                  </Typography>
                  <VideoDialog videoHeading={video1Heading} videoURL={video1URL} videoThumbnail={video1Thumbnail} />
                  <VideoDialog videoHeading={video2Heading} videoURL={video2URL} videoThumbnail={video2Thumbnail} />
                  <VideoDialog videoHeading={video3Heading} videoURL={video3URL} videoThumbnail={video3Thumbnail} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12} sm={3}>
          <Card className={classes.cardRoot} variant="elevation">
            <CardContent className={classes.cardContent}>
              <Typography className={classes.title} color="primary" variant="h5">
                Support
              </Typography>
              <Divider className={classes.divider1} />
              <Typography variant="body1" color="secondary" className={classes.para} gutterBottom>
                If you need assistance with a product evaluation, please start with the&nbsp;
                <a href="https://docs.deltaxml.com/deltajson/" target="_blank" rel="noopener noreferrer" style={{color: DeltaXMLColors.FOUNTAIN_BLUE}}>
                  documentation
                </a>.
              </Typography>
              <Typography variant="body1" color="secondary" className={classes.para} gutterBottom>
                If you can’t find what you need there, please contact us regarding the issue.
              </Typography>
              <Button color="primary" variant="contained" onClick={() => window.open('https://www.deltaxml.com/contact/', '_blank')}>Contact Us</Button>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>
    </Paper>
    :
    <></>
  );
}

export default withStyles(styles)(WelcomeContent);
